import {useEffect, useState} from 'react';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {OperationStateType, OperationStateTypesEnum} from 'presentation/constants';
import {IFormData} from 'presentation/pages/public/sign-up/components/Forms/Step4/schema';
import {translations} from 'presentation/translations-new';
import {formatApiErrorResponse} from 'presentation/utils/formatApiErrorResponse';
import {signUpConfirm} from '../../services/signUpConfirm';
import {useSignUpFormDataStore} from '../../sign-up/store/useSignUpFormDataStore';
import {UseSignupProps} from './types';

const {serverError} = translations['pt-br'].commons;

export function useSignUp({methods}: UseSignupProps) {
	const {
		getValues,
		formState: {isValidating},
	} = methods;

	const [requestState, setRequestState] = useState<OperationStateType | null>(null);
	const [confirmationModalState, setConfirmationModalState] = useState(false);
	const [apiErrorMsg, setApiErrorMsg] = useState('');
	const [errorModalState, setErrorModalState] = useState(false);
	const signUpFormData = useSignUpFormDataStore(state => state.signUpFormData);
	const {cdEmpresaPlano, cdPessoa, celular, cdUsuario, token, dsEmailLogin, cpf} = signUpFormData;
	const formFieldValues = getValues();

	const isDirty = Object.values(formFieldValues).every(val => val !== '');
	const showAPIError = requestState === OperationStateTypesEnum.ERROR && isDirty;
	const showConfirmationModal = requestState === OperationStateTypesEnum.DONE;

	const handleSubmitRequest = async ({password}: IFormData) => {
		try {
			setRequestState(OperationStateTypesEnum.PENDING);
			await signUpConfirm({
				cdPessoa,
				cdSenha: password,
				token,
				cdEmpresaPlano,
				cdUsuario,
				cpf,
				celular,
				dsEmailLogin,
			});
			setRequestState(OperationStateTypesEnum.DONE);
			setConfirmationModalState(true);
		} catch (error) {
			if (error instanceof APIError) {
				setRequestState(OperationStateTypesEnum.ERROR);
				setErrorModalState(true);
				const parsedResponse = formatApiErrorResponse(error);
				setApiErrorMsg(parsedResponse);

				if (error.statusCode === HttpStatusCode.serverError) {
					setApiErrorMsg(serverError);
				}
			}
		}
	};

	const clearAPIError = () => {
		setRequestState(null);
	};

	useEffect(() => {
		if (isValidating) {
			clearAPIError();
		}
	}, [isValidating]);

	return {
		handleSubmitRequest,
		apiErrorMsg,
		showAPIError,
		showConfirmationModal,
		confirmationModalState,
		setConfirmationModalState,
		errorModalState,
		setErrorModalState,
	};
}
